import { fourierConfig as config } from "../fourier/config";
import { pixelTransform } from "../fourier/invaderModel";
import { invaderPixels, invaderVertices } from "../fourier/shapes/invader";
import { FourierTrail } from "../fourier/components/FourierTrail";
import { toSvgPath } from "../fourier/math/epicycles";
import { drawingProgress } from "./hookTiming";
import { hookStage } from "./HookScene";
import { EpicycleChain } from "../fourier/components/EpicycleChain";
import { getDrawing, HookScene } from "./HookScene";
import { hookState, mix, ramp, scatter, type HookPattern } from "./hookTiming";

const { x, y, scale, targetX } = config.layout;
const colors = config.colors;
const pixels = invaderPixels.flatMap((row, py) =>
  [...row].flatMap((v, px) => (v === "1" ? [{ x: px, y: py }] : [])),
);
// The actual first right-angle vertex, mapped by the production pixel transform.
export const corner = {
  x: (invaderVertices[1][0] - pixelTransform.x) * pixelTransform.scale,
  y: (invaderVertices[1][1] - pixelTransform.y) * pixelTransform.scale,
};

export const HookPatterns = ({
  pattern,
  frame,
}: {
  pattern: HookPattern;
  frame: number;
}) => {
  if (frame >= 28) return <HookScene frame={frame} />;
  const state = hookState(pattern, frame);
  const { chain, tip } = getDrawing(frame);
  const end = { x: x + tip.x * scale, y: y + tip.y * scale };
  if (pattern === "A" || pattern === "C") {
    const isCorner = pattern === "A";
    const blend = ramp(frame, isCorner ? 7 : 18, 27);
    const focus = isCorner
      ? { x: targetX + corner.x * scale, y: y + corner.y * scale }
      : end;
    const drift = isCorner ? 0 : Math.sin(Math.PI * ramp(frame, 6, 18));
    const cx = mix(540, focus.x, blend) + drift * 80;
    const cy = mix(960, focus.y, blend) - drift * 50;
    return (
      <g
        transform={`translate(${cx} ${cy}) scale(${state.camera}) translate(${-focus.x} ${-focus.y})`}
      >
        {!isCorner && (
          <g transform={`translate(${x} ${y}) scale(${scale})`}>
            <FourierTrail
              path={toSvgPath(
                hookStage.trail
                  .slice(
                    Math.max(
                      0,
                      Math.floor(
                        (drawingProgress(frame) - 0.025) *
                          config.trailResolution,
                      ),
                    ),
                    Math.floor(
                      drawingProgress(frame) * config.trailResolution,
                    ) + 1,
                  )
                  .concat(tip),
              )}
              opacity={ramp(frame, 6, 10) * (1 - ramp(frame, 20, 27))}
              tipOpacity={0}
              scale={scale}
            />
          </g>
        )}
        <HookScene
          frame={frame}
          chainOpacity={isCorner ? state.chain : ramp(frame, 14, 25)}
          penOpacity={isCorner ? state.chain : 1}
        />
      </g>
    );
  }
  if (pattern === "B") {
    const burst = ramp(frame, 6, 13);
    const gather = ramp(frame, 13, 23);
    const round = ramp(frame, 10, 22);
    return (
      <>
        <HookScene
          frame={frame}
          chainOpacity={state.chain}
          traceOpacity={state.trace}
        />
        <g opacity={state.overlay}>
          {pixels.map((p, i) => {
            const c = chain[i % 12];
            const offset = scatter(i);
            const px = mix(
              540 + (p.x - 5) * 42 + offset.x * burst,
              x + c.center.x * scale,
              gather,
            );
            const py = mix(
              940 + (p.y - 3.5) * 42 + offset.y * burst,
              y + c.center.y * scale,
              gather,
            );
            const size = mix(35, Math.max(4, 2 * c.radius * scale), gather);
            return (
              <rect
                key={i}
                x={px - size / 2}
                y={py - size / 2}
                width={size}
                height={size}
                rx={(size * round) / 2}
                fill={colors.trail}
                fillOpacity={1 - round}
                stroke={colors.circle}
                strokeWidth={mix(0, 2, round)}
                opacity={mix(1, 0.42, gather)}
              />
            );
          })}
        </g>
      </>
    );
  }
  if (pattern === "D") {
    const unfold = ramp(frame, 11, 24);
    const laser = ramp(frame, 0, 8);
    const impact = { x: 540, y: 960 };
    const lx = mix(20, impact.x, laser);
    const ly = mix(550, impact.y, laser);
    const wave = ramp(frame, 8, 20);
    return (
      <>
        <HookScene frame={frame} chainOpacity={0} traceOpacity={state.trace} />
        <g
          transform={`translate(${impact.x} ${impact.y}) scale(${Math.max(0.001, unfold)}) translate(${-impact.x} ${-impact.y})`}
          opacity={state.chain}
        >
          <HookScene frame={frame} traceOpacity={0} />
        </g>
        <g
          opacity={state.overlay * (1 - ramp(frame, 8, 13))}
          stroke={colors.trail}
          strokeLinecap="round"
        >
          <line
            x1={lx - 500}
            y1={ly - 210}
            x2={lx + 220}
            y2={ly + 92}
            strokeWidth={26}
            opacity={0.45}
            filter="url(#impact-glow)"
          />
          <line
            x1={lx - 500}
            y1={ly - 210}
            x2={lx + 220 * (1 - laser)}
            y2={ly + 92 * (1 - laser)}
            strokeWidth={7}
          />
        </g>
        {frame >= 8 && (
          <circle
            cx={impact.x}
            cy={impact.y}
            r={mix(8, 180, wave)}
            fill="none"
            stroke={colors.trail}
            strokeWidth={mix(9, 1, wave)}
            opacity={(1 - wave) * 0.8}
          />
        )}
      </>
    );
  }
  const first = chain[0];
  const open = mix(0.004, 1, ramp(frame, 6, 22));
  const align = ramp(frame, 18, 27);
  const radius = mix(265, first.radius * scale, align);
  return (
    <>
      <HookScene
        frame={frame}
        chainOpacity={state.chain}
        traceOpacity={state.trace}
      />
      <g
        opacity={state.overlay}
        transform={`translate(${mix(540, x + first.center.x * scale, align)} ${mix(960, y + first.center.y * scale, align)})`}
      >
        <ellipse
          rx={radius * open}
          ry={radius}
          fill="none"
          stroke={colors.circle}
          strokeWidth={6}
        />
        <g
          opacity={ramp(frame, 15, 22)}
          transform={`scale(${open * scale} ${scale}) translate(${-first.center.x} ${-first.center.y})`}
        >
          <EpicycleChain chain={chain.slice(0, 1)} />
        </g>
      </g>
    </>
  );
};
